import { logger } from '@lib/logging-system';
import MeProfile from '@pages/User/MeProfile.tsx';
import { lazy } from 'react';

const BidDetail = lazy(() => import('@pages/Courier/BidDetail'));
const BiddingDashboard = lazy(() => import('@pages/Courier/BiddingDashboard'));
const BidsOpen = lazy(() => import('@/components/Bids/BidsOpen'));
const CourierDashboard = lazy(() => import('@pages/Courier/CourierDashboard'));
const DriverAdd = lazy(() => import('@pages/Courier/Drivers/DriverAdd.tsx'));
const DriverAddCompany = lazy(() => import('@pages/Courier/Drivers/DriverAddCompany.tsx'));
const AdminAddCompany = lazy(() => import('@pages/Courier/AdminAddCompany.tsx'));
const DriverDashboard = lazy(() => import('@pages/Driver/DriverDashboard'));
const DeliveryCalendar = lazy(() => import('@pages/Courier/DeliveryCalendar'));
const DeliveryDetail = lazy(() => import('@pages/Courier/DeliveryDetail'));
const DeliveryEdit = lazy(() => import('@pages/Courier/DeliveryEdit'));
const DeliveriesAll = lazy(() => import('@pages/Courier/DeliveriesAll'));
const DriverDeliveryDetail = lazy(() => import('@pages/Courier/Drivers/DriverDeliveryDetail'));
const DriversManage = lazy(() => import('@pages/Courier/Drivers/DriversManage.tsx'));
const FleetManagement = lazy(() => import('@pages/Courier/FleetManagement'));
const LandingPage = lazy(() => import('@pages/LandingPage'));
const LoginBoxed = lazy(() => import('@pages/Authentication/LoginBoxed'));
const NotFound404 = lazy(() => import('@pages/NotFound404.tsx'));
const Pricing = lazy(() => import('@pages/LandingPage/Pricing'));
const Register = lazy(() => import('@pages/LandingPage/Register'));
const RecoverIdBoxed = lazy(() => import('@pages/Authentication/RecoverIdBox'));
const RegisterBoxed = lazy(() => import('@pages/Authentication/RegisterBoxed'));
const ShipperDashboard = lazy(() => import('@pages/Shipper/ShipperDashboard'));
const UnlockBoxed = lazy(() => import('@pages/Authentication/UnlockBox'));
const VehicleAdd = lazy(() => import('@pages/Courier/Vehicle/VehicleAdd.tsx'));
const VehicleManage = lazy(() => import('@pages/Courier/Vehicle/VehicleManage.tsx'));
const VehicleTypeAdd = lazy(() => import('@pages/Courier/Vehicle/VehicleTypeAdd.tsx'));
const Clients = lazy(() => import('@pages/Courier/Directories/Clients.tsx'));
const Addresses = lazy(() => import('@pages/Courier/Directories/Addresses.tsx'));
const AddressAdd = lazy(() => import('@pages/Courier/Deliveries/AddressAdd.tsx'));
const UserProfile = lazy(() => import('@/pages/AccountPages/UserProfile'));
const UserAccountSettings = lazy(() => import('@/pages/AccountPages/UserAccountSettings'));
const CompanyProfile = lazy(() => import('@/pages/AccountPages/CompanyProfile'));
const CourierBoost = lazy(() => import('@pages/LandingPage/CourierBoost'));
const FAQs = lazy(() => import('@pages/LandingPage/FAQs'));
const ContactUs = lazy(() => import('@pages/LandingPage/ContactUs'));
const ParcelAdd = lazy(() => import('@pages/Courier/Parcels/ParcelAdd'));
const ParcelTrack = lazy(() => import('@pages/Courier/Parcels/ParcelTrack'));
const CompanyDashboard = lazy(() => import('@/pages/Company/CompanyDashboard'));
const DeliveryAddEasy = lazy(() => import('@pages/Courier/Deliveries/DeliveryAddEasy'));
const CourierAdvance = lazy(() => import('@pages/LandingPage/CourierAdvance'));
const Permissions = lazy(() => import('@pages/CBAdmin/Permissions'));
const ListInvoices = lazy(() => import('@pages/CBAdmin/ListInvoices'));
const CAUsers = lazy(() => import('@pages/CBAdmin/CAUsers'));

// Payout pages
const PayoutDashboard = lazy(() => import('@pages/Courier/Payouts/PayoutDashboard'));
const PendingPayouts = lazy(() => import('@pages/Courier/Payouts/PendingPayouts'));
const PayoutHistory = lazy(() => import('@pages/Courier/Payouts/PayoutHistory'));

// CourierAdvance pages
const CreditDashboard = lazy(() => import('@pages/Courier/CourierAdvance/CreditDashboard'));
const ApprovedTerms = lazy(() => import('@pages/Courier/CourierAdvance/ApprovedTerms'));
const ApplicationForm = lazy(() => import('@pages/Courier/CourierAdvance/ApplicationForm'));

// Additional page components that can work as standalone routes
const DeliveryAdd = lazy(() => import('@pages/Courier/Deliveries/DeliveryAdd'));

// Product Management pages
const ProductPricing = lazy(() => import('@pages/ProductManagement/Pricing'));
const ProductEdit = lazy(() => import('@pages/ProductManagement/ProductEdit'));

// Invoice pages
const InvoiceList = lazy(() => import('@pages/Courier/Invoicing/InvoiceList'));
const InvoiceCreate = lazy(() => import('@pages/Courier/Invoicing/InvoiceCreate'));
// TODO: Create these components
// const InvoiceDetail = lazy(() => import('@pages/Courier/Invoicing/InvoiceDetail'));
// const InvoicePayment = lazy(() => import('@pages/Courier/Invoicing/InvoicePayment'));

const RoutesArray = [
  // Login
  // {
  //   path: '/',
  //   element: <LandingPage />,
  //   layout: 'blank',
  // }, // Courier Dashboard
  {
    path: '/',
    element: <CourierBoost />,
    layout: 'blank',
  }, // Sign Up Portal
  {
    path: '/CourierDashboard',
    element: <CourierDashboard />,
    layout: 'default',
  }, // Bidding Dashboard
  {
    path: '/BiddingDashboard',
    element: <BiddingDashboard />,
    layout: 'default',
  },
  {
    path: '/CompanyDashboard',
    element: <CompanyDashboard />,
    layout: 'easy-mode',
  },
  {
    path: '/BidsOpen',
    element: <BidsOpen />,
    layout: 'default',
  },
  {
    path: '/DeliveryCalendar',
    element: <DeliveryCalendar />,
    layout: 'easy-mode',
  },
  {
    path: '/DeliveriesAll',
    element: <DeliveriesAll />,
    layout: 'easy-mode',
  },
  {
    path: '/DeliveryDetail/:uuid',
    element: <DeliveryDetail />,
    layout: 'easy-mode',
  },
  {
    path: '/DeliveryEdit/:uuid',
    element: <DeliveryEdit />,
    layout: 'easy-mode',
  },
  {
    path: '/DriverDeliveryDetail/:deliveryUuid',
    element: <DriverDeliveryDetail />,
    layout: 'easy-mode',
  },
  {
    path: '/FleetManagement',
    element: <FleetManagement />,
    layout: 'default',
  },
  {
    path: '/VehicleAdd',
    element: <VehicleAdd />,
    layout: 'easy-mode',
  },
  {
    path: '/VehicleManage',
    element: <VehicleManage />,
    layout: 'easy-mode',
  },
  {
    path: '/VehicleTypeAdd',
    element: <VehicleTypeAdd />,
    layout: 'easy-mode',
  },
  {
    path: '/Clients',
    element: <Clients />,
    layout: 'easy-mode',
  },
  {
    path: '/Addresses',
    element: <Addresses />,
    layout: 'easy-mode',
  },
  {
    path: '/AddressAdd',
    element: (
      <AddressAdd
        onSubmitAddress={() => logger.info('[RoutesArray] Address submitted')}
        onCancelAddress={() => window.history.back()}
      />
    ),
    layout: 'easy-mode',
  },
  {
    path: '/DriversManage',
    element: <DriversManage />,
    layout: 'easy-mode',
  }, // Add Drivers
  {
    path: '/DriverAdd',
    element: <DriverAdd />,
    layout: 'easy-mode',
  },
  {
    path: '/DriverAddCompany',
    element: <DriverAddCompany />,
    layout: 'easy-mode',
  },
  {
    path: '/AdminAddCompany',
    element: <AdminAddCompany />,
    layout: 'easy-mode',
  },
  {
    path: '/DriverDashboard',
    element: <DriverDashboard />,
    layout: 'easy-mode',
  },
  {
    path: '/Permissions',
    element: <Permissions />,
    layout: 'cbadmin',
  },
  {
    path: '/CAUsers',
    element: <CAUsers />,
    layout: 'cbadmin',
  },
  {
    path: '/ListInvoices',
    element: <ListInvoices />,
    layout: 'cbadmin',
  },
  {
    path: '/PayoutDashboard',
    element: <PayoutDashboard />,
    layout: 'cbadmin',
  },
  {
    path: '/PendingPayouts',
    element: <PendingPayouts />,
    layout: 'cbadmin',
  },
  {
    path: '/PayoutHistory',
    element: <PayoutHistory />,
    layout: 'cbadmin',
  },
  {
    path: '/Courier/CourierAdvance/CreditDashboard',
    element: <CreditDashboard />,
    layout: 'easy-mode',
  },
  {
    path: '/Courier/CourierAdvance/ApprovedTerms',
    element: <ApprovedTerms />,
    layout: 'easy-mode',
  },
  {
    path: '/Courier/CourierAdvance/ApplicationForm',
    element: <ApplicationForm />,
    layout: 'easy-mode',
  },
  {
    path: '/BidDetail',
    element: <BidDetail />,
    layout: 'default',
  },
  {
    path: '/LandingPage',
    element: <LandingPage />,
    layout: 'blank',
  },
  {
    path: '/ShipperDashboard',
    element: <ShipperDashboard />,
    layout: 'easy-mode',
  }, //Authentication
  {
    path: '/Users/Me',
    element: <MeProfile />,
    layout: 'default',
  }, //Authentication
  {
    path: '/login',
    element: <LoginBoxed />,
    layout: 'blank',
  },
  {
    path: '/auth/boxed-signin',
    element: <LoginBoxed />,
    layout: 'blank',
  },
  {
    path: '/Authentication/RegisterBoxed',
    element: <RegisterBoxed />,
    layout: 'blank',
  },
  {
    path: '/auth/boxed-lockscreen',
    element: <UnlockBoxed />,
    layout: 'blank',
  },
  {
    path: '/auth/boxed-password-reset',
    element: <RecoverIdBoxed />,
    layout: 'blank',
  },
  {
    path: '/AccountPages/UserProfile',
    element: <UserProfile />,
    layout: 'easy-mode',
  },
  {
    path: '/AccountPages/UserAccountSettings',
    element: <UserAccountSettings />,
    layout: 'easy-mode',
  },
  {
    path: '/AccountPages/CompanyProfile',
    element: <CompanyProfile />,
    layout: 'easy-mode',
  },
  {
    path: '/CourierBoost',
    element: <CourierBoost />,
    layout: 'blank',
  },
  {
    path: '/CourierAdvance',
    element: <CourierAdvance />,
    layout: 'blank',
  },
  {
    path: '/FAQs',
    element: <FAQs />,
    layout: 'blank',
  },
  {
    path: '/Pricing',
    element: <Pricing />,
    layout: 'blank',
  },
  {
    path: '/Register',
    element: <Register />,
    layout: 'blank',
  },
  {
    path: '/ContactUs',
    element: <ContactUs />,
    layout: 'blank',
  },
  {
    path: '/ParcelAdd',
    element: <ParcelAdd onSubmitParcel={() => logger.info('[RoutesArray] Standalone Parcel Submit')} />,
    layout: 'easy-mode',
  },
  {
    path: '/ParcelTrack',
    element: <ParcelTrack />,
    layout: 'easy-mode',
  },
  {
    path: '/DeliveryAddEasy',
    element: <DeliveryAddEasy />,
    layout: 'easy-mode',
  },
  // Additional delivery route
  {
    path: '/DeliveryAdd',
    element: <DeliveryAdd />,
    layout: 'default',
  },
  // Product Management routes
  {
    path: '/ProductManagement/Pricing',
    element: <ProductPricing />,
    layout: 'easy-mode',
  },
  {
    path: '/ProductManagement/ProductEdit/:id',
    element: <ProductEdit />,
    layout: 'easy-mode',
  },
  // Invoice pages
  {
    path: '/app/courier/invoicing',
    element: <InvoiceList />,
    layout: 'default',
  },
  {
    path: '/app/courier/invoicing/create',
    element: <InvoiceCreate />,
    layout: 'default',
  },
  // TODO: Uncomment when components are created
  // {
  //   path: '/app/courier/invoicing/:invoiceUuid',
  //   element: <InvoiceDetail />,
  //   layout: 'default',
  // },
  // {
  //   path: '/app/courier/invoicing/:invoiceUuid/payment',
  //   element: <InvoicePayment />,
  //   layout: 'default',
  // },
  {
    path: '/*',
    element: <NotFound404 />,
    layout: 'blank',
  },
];

export { RoutesArray };
